<template>
  <div
    v-if="ready"
    class="local-patient-list"
  >
    <v-container v-if="!showSearchForm">
      <v-layout
        v-if="projects.length <= 0"
        justify-center
        align-center
      >
        <v-banner single-line>
          <h1>Aucune fiche disponible</h1>
        </v-banner>
      </v-layout>
      <project-list-items
        v-else
        :projects="projects"
        show-sync
        show-delete
        @name-click="goToProjectPage"
        @sync="sync"
        @delete-project="deleteProject"
      />

      <v-snackbar
        v-model="showSnackbar"
        :color="colorSnackbar"
        :timeout="timeoutSnackbar"
        top
      >
        {{ textSnackbar }}
        <v-btn
          text
          @click="showSnackbar = false"
        >
          Fermer
        </v-btn>
      </v-snackbar>

      <div class="save-container">
        <v-btn
          class="save"
          @click="goToNewProjectPage"
        >
          Nouvelle Fiche
        </v-btn>
        <v-btn
          class="save"
          :disabled="!isOnline"
          @click="goToRemoteList"
        >
          Base serveur
        </v-btn>
        <v-btn
          v-if="!searchActivated"
          class="save"
          @click="showSearchForm = true"
        >
          Recherche
        </v-btn>
        <v-btn
          v-if="searchActivated"
          class="save"
          @click="cleanSearch"
        >
          Annuler la recherche
        </v-btn>
      </div>
    </v-container>

    <v-container v-if="showSearchForm">
      <search-form
        @cancel="showSearchForm = false"
        @search="search"
      />
    </v-container>
  </div>
</template>

<script>
import { mapActions } from "vuex";

import { synchronization } from "@/mixins/synchronization";

import ProjectListItems from "../components/ProjectListItems";
import SearchForm from "../components/SearchForm";

export default {
  name: "LocalPatientList",
  components: {
    ProjectListItems,
    SearchForm
  },
  mixins: [synchronization],
  data() {
    return {
      ready: false,
      projects: [],
      patients: [],

      // Show search form
      showSearchForm: false,
      searchActivated: false,

      showSnackbar: false,
      textSnackbar: "",
      colorSnackbar: "success",
      timeoutSnackbar: 2000
    };
  },

  mounted() {
    this.getDataFromDB(); // Retrieve projects/Patients from IndexedDB
  },

  methods: {
    ...mapActions({
      createProject: "projects/createProject",
      createPatient: "patients/createPatient",
      createMinerva: "minerva/createMinerva",
      createMoldedSeat: "moldedSeat/createMoldedSeat",
      updateProject: "projects/updateProject",
      getProject: "projects/getProject",
      getLocalPatient: "patients/getLocalPatient",
      getLocalMinerva: "minerva/getLocalMinerva",
      getLoggedInUser: "auth/getLoggedInUser",
      deleteLocalProject: "projects/deleteLocalProject"
    }),

    // Get all projects/patients from IndexedDB
    async getDataFromDB() {
      const projects = await this.$db.projects.toArray();

      // Order by created_at
      projects.sort(function(a, b) {
        a = new Date(a.created_at);
        b = new Date(b.created_at);
        return a > b ? -1 : a < b ? 1 : 0;
      });

      const patients = await this.$db.patients.toArray();
      this.projects = projects;
      this.patients = patients;
      this.ready = true;
    },

    // Create product based on project product model
    async createProduct(project) {
      var user;

      switch (project.product_model) {
        case "NECK_BRACE":
          var minerva = await this.$db.minerva.get(project.local_version);
          user = await this.getLocalUser();
          minerva.user_id = user.id;
          return await this.createMinerva(minerva);
        case "MOLDED_SEAT":
          var moldedSeat = await this.$db.molded_seat.get(
            project.local_version
          );
          user = await this.getLocalUser();
          moldedSeat.user_id = user.id;
          return await this.createMoldedSeat(moldedSeat);

        default:
          break;
      }
    },

    // Check if project exist in API
    async projectExist(projectUuid) {
      try {
        const project = await this.getProject(projectUuid);
        return project;
      } catch (error) {
        console.log(error);
        if (error.response.status === 404) {
          return false;
        }
      }
    },

    // Sync local data with API
    async sync(project) {
      try {
        await this.synchronize(project);
        await this.getDataFromDB();

        this.textSnackbar = "Synchronisation réalisée avec succès";
        this.colorSnackbar = "success";
        this.timeoutSnackbar = 2000;
        this.showSnackbar = true;
      } catch (error) {
        if (
          error.response.data.error &&
          (error.response.data.error == "ERR_CANNOT_SEE_PROJECT" ||
            error.response.data.error == "ERR_CANNOT_SAVE_PRODUCT" ||
            error.response.data.error == "ERR_CANNOT_MODIFY_PROJECT")
        ) {
          this.timeoutSnackbar = 0;
          this.textSnackbar = "Cette fiche ne peut plus être modifiée";
          this.colorSnackbar = "error";
          this.showSnackbar = true;
          // await this.$db.projects.delete(project.uuid);
          await this.getDataFromDB();

          return;
        }

        this.textSnackbar = "Erreur de synchronisation : fiche invalide";
        if (
          error.response != undefined &&
          error.response.data != undefined &&
          error.response.data.message != undefined
        ) {
          this.textSnackbar =
            "Erreur de synchronisation : " +
            error.response.data.message +
            "(1.7.4)";

          if (error.response.data.message.match(/given data was/) != null) {
            this.textSnackbar =
              "Erreur de synchronisation : " +
              JSON.stringify(error.response.data) +
              "(@version@) (data)";
          }
        }
        this.colorSnackbar = "error";
        this.timeoutSnackbar = 0;
        this.showSnackbar = true;
      }
    },

    // Perform projects/patients search
    async search(searchForm) {
      let patients = await this.$db.patients.toArray();
      let projects = await this.$db.projects.toArray();

      // Check dateFrom dataTo
      if (searchForm.dateFrom && searchForm.dateTo) {
        projects = projects.filter(
          project =>
            Date.parse(project.created_at) >= Date.parse(searchForm.dateFrom) &&
            Date.parse(project.created_at) <= Date.parse(searchForm.dateTo)
        );
      }

      // Check orthaoNumber
      if (searchForm.orthaoNumber) {
        projects = projects.filter(
          project =>
            project.file_number.toLowerCase() ==
            searchForm.orthaoNumber.toLowerCase()
        );
      }

      // Check patientLastname
      if (searchForm.patientLastname) {
        patients = patients.filter(
          patient =>
            patient.lastname.toLowerCase() ==
            searchForm.patientLastname.toLowerCase()
        );
        let newProjects = [];
        patients.forEach(patient => {
          const project = projects.find(
            project => project.patient_uuid == patient.uuid
          );
          if (project) {
            newProjects.push(project);
          }
        });
        projects = newProjects;
      }

      // Check patientLastname
      if (searchForm.patientFirstname) {
        patients = patients.filter(
          patient =>
            patient.firstname.toLowerCase() ==
            searchForm.patientFirstname.toLowerCase()
        );
        let newProjects = [];
        patients.forEach(patient => {
          const project = projects.find(
            project => project.patient_uuid == patient.uuid
          );
          if (project) {
            newProjects.push(project);
          }
        });
        projects = newProjects;
      }

      this.projects = projects;
      this.searchActivated = true;
      this.showSearchForm = false;
    },

    // Clean search
    async cleanSearch() {
      const projects = await this.$db.projects.toArray();
      this.projects = projects;
      this.searchActivated = false;
    },

    // Get patient info
    patient(patientUuid) {
      if (this.patients.length > 0) {
        const patient = this.patients.find(
          patient => patient.uuid == patientUuid
        );
        if (patient) {
          return patient;
        }
      }
    },

    // Get patient info
    getPatientLastname(patientUuid) {
      if (this.patients.length > 0) {
        return this.patients.find(patient => patient.uuid == patientUuid)
          .lastname;
      }
    },

    // Go to New Project Page
    goToProjectPage(projectUuid) {
      this.$router.push({
        name: "ProjectPage",
        params: { projectUuid: projectUuid }
      });
    },

    // Go to new project page
    goToNewProjectPage() {
      console.log("test");

      this.$router.push({ name: "CreateProject" });
    },

    // Go to remote list
    goToRemoteList() {
      this.$router.push({ name: "RemotePatientList" });
    },

    async deleteProject(project) {
      try {
        await this.deleteLocalProject(project.uuid);
      } catch {
        console.log("Error on delete");
      }
      this.getDataFromDB();
    }
  }
};
</script>

<style lang="scss">
.local-patient-list {
  height: 100%;

  .container {
    height: 100%;
    padding: 0;

    .layout {
      height: calc(100% - 60px);
    }
  }
}
</style>
